/**
 * Core project types — spec §3.3 Data Architecture
 * projects/{projectId} in Firestore
 */

import type { Timestamp } from "firebase/firestore";

// ─── State Machine ─────────────────────────────────────────────
// Spec §3.1: 19 states (18 pipeline + ERROR)
// Every state transition writes to state_log subcollection

export type StateMachineState =
  | "CREATED"
  | "INGESTED"
  | "TRANSCRIBING"
  | "TRANSCRIBED"
  | "ANALYZING"
  | "ANALYZED"
  | "STRUCTURING"
  | "STRUCTURED"
  | "DRAFTING"
  | "DRAFTED"
  | "AWAITING_REVIEW"
  | "REVISED"
  | "AUTO_ADVANCED"
  | "EDITING"
  | "EDITED"
  | "PACKAGING"
  | "PACKAGED"
  | "PUBLISHING"
  | "PUBLISHED"
  | "ERROR";

// ─── Pricing & Config ──────────────────────────────────────────
// Spec §10.1: Four revenue tiers

export type PricingTier = "production" | "publishing" | "premium";

export type TrimSize = "5.5x8.5" | "6x9";

export type PaperStock = "cream" | "white";

export type Genre =
  | "memoir"
  | "biography"
  | "business"
  | "self_help"
  | "motivational"
  | "family_history"
  | "other";

export type NarrativeArc =
  | "chronological"
  | "thematic"
  | "episodic"
  | "hybrid";

export type SourceOption = "audio" | "notes" | "manuscript" | "dictation";

export type CoverDirection = "editorial" | "photographic" | "illustrated" | "minimal";

export type DistributionTarget = "amazon_kdp" | "global_print" | "direct_sales" | "audiobook";

export type IsbnMode = "pullpress" | "own";

// ─── Project Settings ──────────────────────────────────────────

export interface ProjectSettings {
  genre: Genre;
  trim_size: TrimSize;
  paper_stock: PaperStock;
  review_deadline_days: number; // 14–30 per spec §4.6
  narrative_arc_preference?: NarrativeArc;
  target_word_count?: number; // 30,000–80,000 per A-004
  source_option: SourceOption;
  subtitle: string;
  audience: string;
  goal: string;
  cover_direction: CoverDirection;
  distribution_targets: readonly DistributionTarget[];
  isbn_mode: IsbnMode;
}

// ─── Core Project Document ─────────────────────────────────────
// Firestore: projects/{projectId}

export interface Project {
  id: string;
  title: string;
  author_name: string;
  author_id: string; // Firebase Auth UID
  status: ProjectStatus;
  state: StateMachineState;
  settings: ProjectSettings;
  pricing_tier: PricingTier;
  isbn?: string;
  paid_tiers?: string[];
  payment_status?: "unpaid" | "checkout_created" | "paid" | "failed";
  created_at: Timestamp;
  updated_at: Timestamp;
  review_deadline?: Timestamp;
  auto_advanced: boolean;
  error_message?: string;
  error_stage?: string;
}

export type ProjectStatus = "active" | "completed" | "error" | "archived";

// ─── State Transition ──────────────────────────────────────────
// Firestore: projects/{projectId}/state_log/{logId}

export interface StateTransitionLog {
  id: string;
  from_state: StateMachineState;
  to_state: StateMachineState;
  trigger: string;
  timestamp: Timestamp;
  metadata?: Record<string, unknown>;
}

// ─── Valid Transitions Map ─────────────────────────────────────
// Spec §3.1 — enforced by Orchestrator

export const VALID_TRANSITIONS: Record<StateMachineState, StateMachineState[]> = {
  CREATED: ["INGESTED", "ERROR"],
  INGESTED: ["TRANSCRIBING", "ERROR"],
  TRANSCRIBING: ["TRANSCRIBED", "ERROR"],
  TRANSCRIBED: ["ANALYZING", "ERROR"],
  ANALYZING: ["ANALYZED", "ERROR"],
  ANALYZED: ["STRUCTURING", "ERROR"],
  STRUCTURING: ["STRUCTURED", "ERROR"],
  STRUCTURED: ["DRAFTING", "ERROR"],
  DRAFTING: ["DRAFTED", "ERROR"],
  DRAFTED: ["AWAITING_REVIEW", "ERROR"],
  AWAITING_REVIEW: ["REVISED", "AUTO_ADVANCED", "ERROR"],
  REVISED: ["EDITING", "ERROR"],
  AUTO_ADVANCED: ["EDITING", "ERROR"],
  EDITING: ["EDITED", "ERROR"],
  EDITED: ["PACKAGING", "ERROR"],
  PACKAGING: ["PACKAGED", "ERROR"],
  PACKAGED: ["PUBLISHING", "ERROR"],
  PUBLISHING: ["PUBLISHED", "ERROR"],
  PUBLISHED: [],
  ERROR: [],
};

// All 19 states for validation
export const ALL_STATES: StateMachineState[] = [
  "CREATED",
  "INGESTED",
  "TRANSCRIBING",
  "TRANSCRIBED",
  "ANALYZING",
  "ANALYZED",
  "STRUCTURING",
  "STRUCTURED",
  "DRAFTING",
  "DRAFTED",
  "AWAITING_REVIEW",
  "REVISED",
  "AUTO_ADVANCED",
  "EDITING",
  "EDITED",
  "PACKAGING",
  "PACKAGED",
  "PUBLISHING",
  "PUBLISHED",
  "ERROR",
];
